import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  QrCode
} from 'lucide-react';

const WhatsAppIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pickupCity: '',
    destinationCountry: '',
    shipmentType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Construct WhatsApp Message
    const text = `*New Inquiry from Website*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Phone:* ${formData.phone}\n` +
      `*City:* ${formData.pickupCity}\n` +
      `*Destination:* ${formData.destinationCountry}\n` +
      `*Type:* ${formData.shipmentType}\n` +
      `*Message:* ${formData.message}`;

    const encodedText = encodeURIComponent(text);
    const whatsappUrl = `https://wa.me/918847428801?text=${encodedText}`;

    // Open WhatsApp
    window.open(whatsappUrl, '_blank');

    // Show success message
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        pickupCity: '',
        destinationCountry: '',
        shipmentType: '',
        message: ''
      });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      details: ['+91 8847428801'],
      action: 'tel:+918847428801'
    },
    {
      icon: <WhatsAppIcon size={24} />,
      title: 'WhatsApp',
      details: ['+91 8847428801'],
      action: 'https://wa.me/918847428801'
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      details: ['packnfly96@gmail.com'],
      action: 'mailto:packnfly96@gmail.com'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Address',
      details: ['Plot No 5A, Industrial Area', 'Phase 2, Panchkula, Haryana'],
      action: 'https://www.google.com/maps/search/Plot+No+5A+Industrial+Area+Phase+2+Panchkula+Haryana'
    },
    {
      icon: <Clock size={24} />,
      title: 'Business Hours',
      details: ['Monday - Saturday: 9:00 AM - 7:00 PM', 'Sunday: Closed'],
      action: null
    }
  ];

  const services = [
    'Document Courier',
    'Parcel Courier',
    'Medicine Courier',
    'Food Items Courier',
    'Commercial Shipment',
    'Excess Baggage'
  ];

  const pickupCities = [
    'Chandigarh',
    'Mohali',
    'Panchkula',
    'Zirakpur',
    'Other'
  ];

  const destinationCountries = [
    'USA',
    'Canada',
    'United Kingdom',
    'Australia',
    'Dubai / UAE',
    'Germany',
    'Europe (Other)',
    'Other'
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <div className="contact-hero-row">
              <h1>Contact Us</h1>
              <div className="qr-inline">
                <img
                  src="/images/qr-code-contact-packnfly.png"
                  alt="Contact QR Code - Scan for Quick Contact"
                  className="contact-qr-code"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="qr-code-placeholder" style={{ display: 'none' }}>
                  <QrCode size={100} color="rgba(255, 255, 255, 0.8)" />
                  <p className="qr-text">Scan for Quick Contact</p>
                </div>
              </div>
              <p className="contact-hero-tagline">Get in touch with our logistics experts today</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="contact-info">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Get in Touch</h2>
            <p>Multiple ways to reach us for your international courier needs</p>
          </motion.div>

          <div className="contact-grid">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`contact-card ${info.title === 'WhatsApp' ? 'whatsapp-card' : ''}`}
              >
                <div className="contact-icon">
                  {info.icon}
                </div>
                <h3>{info.title}</h3>
                {info.details.map((detail, detailIndex) => (
                  <p key={detailIndex}>{detail}</p>
                ))}
                {info.action && (
                  <a
                    href={info.action}
                    className="contact-link"
                    target={info.action.startsWith('https') ? '_blank' : undefined}
                    rel={info.action.startsWith('https') ? 'noopener noreferrer' : undefined}
                  >
                    {info.title === 'Phone' ? 'Call Now' :
                      info.title === 'WhatsApp' ? 'Chat on WhatsApp' :
                        info.title === 'Email' ? 'Send Email' : 'View on Map'}
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact-form-section">
        <div className="container">
          <div className="form-content">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="form-text"
            >
              <h2>Send Us a Message</h2>
              <p>
                Ready to ship internationally? Fill out the form below and our team will
                get back to you within 24 hours with the best rates for your shipment from Chandigarh.
              </p>

              <div className="form-features">
                <div className="feature">
                  <CheckCircle />
                  <span>Quick Response</span>
                </div>
                <div className="feature">
                  <CheckCircle />
                  <span>Free Quote</span>
                </div>
                <div className="feature">
                  <CheckCircle />
                  <span>Expert Guidance</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="contact-form"
            >
              {isSubmitted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="success-message"
                >
                  <CheckCircle size={48} />
                  <h3>Thank You!</h3>
                  <p>Your message has been sent successfully. We'll get back to you soon with the best rates.</p>
                </motion.div>
              )}

              <form
                name="contact-form"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className={isSubmitted ? 'hidden' : ''}
              >
                <input type="hidden" name="form-name" value="contact-form" />
                <input type="hidden" name="bot-field" />

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Mobile Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="10-digit mobile number"
                      pattern="[0-9]{10}"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pickupCity">Pickup City</label>
                    <select
                      id="pickupCity"
                      name="pickupCity"
                      value={formData.pickupCity}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Pickup City</option>
                      {pickupCities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="destinationCountry">Destination Country</label>
                    <select
                      id="destinationCountry"
                      name="destinationCountry"
                      value={formData.destinationCountry}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Destination</option>
                      {destinationCountries.map((country) => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="shipmentType">Shipment Type</label>
                    <select
                      id="shipmentType"
                      name="shipmentType"
                      value={formData.shipmentType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Type</option>
                      {services.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your international courier requirements..."
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary">
                  Send Message
                  <Send size={18} />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Google Map */}
      <section className="contact-map-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Our Service Area</h2>
            <p>We provide free doorstep pickup from Chandigarh, Mohali, Panchkula, and Zirakpur</p>
          </motion.div>
          <div className="contact-map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d109741.02005536151!2d76.68839365!3d30.7363!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390fed0be66ec96b%3A0xa5ff67f9527319fe!2sChandigarh!5e0!3m2!1sen!2sin!4v1707200000000"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '1rem' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Service Area - Chandigarh"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Frequently Asked Questions</h2>
            <p>Quick answers to common questions about our international courier services</p>
          </motion.div>

          <div className="faq-grid">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="faq-item"
            >
              <h3>How are international courier charges calculated?</h3>
              <p>International courier charges are based on the weight of the shipment (actual or volumetric, whichever is higher), destination country, and type of shipment. We offer competitive rates starting from affordable prices. Contact us for an exact quote.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="faq-item"
            >
              <h3>How many days does delivery take?</h3>
              <p>Delivery typically takes 3–7 business days depending on the destination country. Express delivery to Dubai/UAE takes 3–5 days, UK takes 4–6 days, and USA/Canada/Australia takes 5–8 days. You will receive tracking details to monitor your shipment.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="faq-item"
            >
              <h3>Is medicine courier allowed internationally?</h3>
              <p>Yes, we do courier medicines internationally. However, medicines must be properly prescribed, sealed in original packaging, and accompanied by a valid prescription. Some countries have specific regulations which our team will guide you through.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="faq-item"
            >
              <h3>Will I get tracking details?</h3>
              <p>Absolutely! Every shipment gets a unique tracking number. You can track your parcel in real-time from pickup to delivery. We also send regular updates via WhatsApp and SMS.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="faq-item"
            >
              <h3>Is home pickup available in my area?</h3>
              <p>Yes! We provide free doorstep pickup from Chandigarh, Mohali, Panchkula, Zirakpur, and surrounding areas. Just call or WhatsApp us and our executive will visit your location at your preferred time.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="faq-item"
            >
              <h3>What documents are required for international shipping?</h3>
              <p>For international shipments, you'll need a valid ID proof, invoice (for commercial shipments), and customs declaration forms. Our team will guide you through all required documentation based on your destination country.</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
